import type { PublicKey } from "@solomon-labs/publickey";
import { ConnectionManager } from "@solomon-labs/solana-connection";

export interface WalletStats {
    wallet_age_days: number;
    wallet_tx_count: number;
}

const SIGNATURE_LIMIT = 1000;
const TIMEOUT_MS = 5_000;
const SECONDS_PER_DAY = 86_400;

const ZERO_STATS: WalletStats = {
    wallet_age_days: 0,
    wallet_tx_count: 0
};

/**
 * On-chain wallet provenance for sybil-filter cohorts. Returns:
 *  - `wallet_tx_count` — capped at SIGNATURE_LIMIT (1000); we only need a
 *    "real wallet" floor, not exact counts. Wallets above the cap will all
 *    report 1000 — fine for the >=3 threshold the cohort uses.
 *  - `wallet_age_days` — days since the oldest known signature. Zero if the
 *    wallet has no signatures or the RPC doesn't return blockTime.
 *
 * Never throws. RPC failures, timeouts, and missing data all collapse to
 * `ZERO_STATS` with a console.warn — analytics must never block UX.
 */
export async function fetchWalletStats(publicKey: PublicKey): Promise<WalletStats> {
    try {
        return await Promise.race([
            queryStats(publicKey),
            new Promise<WalletStats>((_, reject) =>
                setTimeout(() => reject(new Error("fetchWalletStats: timeout")), TIMEOUT_MS)
            )
        ]);
    } catch (err) {
        console.warn("fetchWalletStats failed:", err);
        return ZERO_STATS;
    }
}

async function queryStats(publicKey: PublicKey): Promise<WalletStats> {
    const client = ConnectionManager.getRpcClient();
    const result = await client.getSignaturesForAddress(publicKey, {
        limit: SIGNATURE_LIMIT
    });
    // The RPC client returns either ConfirmedSignatureInfo[] (default) or
    // ArrayBuffer (when binary mode is requested). We never request binary,
    // but the return type union forces this guard.
    if (!Array.isArray(result) || result.length === 0) {
        return ZERO_STATS;
    }
    const oldest = result[result.length - 1];
    const blockTime = oldest?.blockTime;
    if (typeof blockTime !== "number" || blockTime <= 0) {
        return { wallet_age_days: 0, wallet_tx_count: result.length };
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    const ageDays = Math.max(0, Math.floor((nowSeconds - blockTime) / SECONDS_PER_DAY));
    return { wallet_age_days: ageDays, wallet_tx_count: result.length };
}
